/**
 * The conversation builder's component catalog, as `GET /api/agent-components`
 * serves it.
 *
 * WHAT IS NOT HERE: the list of component keys. The catalog is the only place
 * the builder learns which steps exist, so a copy of the list in this package
 * would be a second list that goes stale. `key` is a plain string on the wire.
 *
 * WHAT IS: the closed vocabularies a step is DESCRIBED in — kinds of field,
 * channels, directions. These mirror `@vagusx/agent-core/v2`'s
 * `component-meta.ts`, which this package cannot import, and the API's
 * `vocabulary.test.ts` fails the moment the two disagree. The response is also
 * parsed through these schemas before it leaves the API, so a meta the web app
 * could not render is refused at the server rather than drawn wrong.
 */

import { z } from 'zod';

export const COMPONENT_CHANNELS = ['voice', 'sms', 'chat'] as const;
export type ComponentChannel = (typeof COMPONENT_CHANNELS)[number];

export const COMPONENT_CALL_DIRECTIONS = ['inbound', 'outbound'] as const;
export type ComponentCallDirection = (typeof COMPONENT_CALL_DIRECTIONS)[number];

export const COMPONENT_CATEGORIES = [
  'open',
  'identity',
  'collect',
  'schedule',
  'inform',
  'handoff',
  'decide',
  'close',
] as const;
export type ComponentCategory = (typeof COMPONENT_CATEGORIES)[number];

export const COMPONENT_FIELD_KINDS = [
  'text',
  'textarea',
  'number',
  'duration',
  'boolean',
  'select',
  'multi',
  'locale-text',
  'field-list',
  'runtime-key',
  'custom',
] as const;
export type ComponentFieldKind = (typeof COMPONENT_FIELD_KINDS)[number];

export const COMPONENT_OPTION_SOURCES = ['providers', 'locations', 'appointmentTypes'] as const;
export type ComponentOptionSource = (typeof COMPONENT_OPTION_SOURCES)[number];

/** Every one of these needs an editor in the web app's builder. */
export const COMPONENT_CUSTOM_EDITORS = ['facts', 'text-list', 'line-list'] as const;
export type ComponentCustomEditor = (typeof COMPONENT_CUSTOM_EDITORS)[number];

export const LOCALE_TEXT_STORAGE = ['slot', 'literal'] as const;
export type LocaleTextStorage = (typeof LOCALE_TEXT_STORAGE)[number];

/**
 * The caller requests a shared tool lets them make during any step. Only the
 * tool's own outcome carries one, so a step's ending that merely shares an id
 * (`AnythingElse`'s own `deferred`) is never read as a request (ADR-276).
 */
export const COMPONENT_SHARED_EXITS = ['handoff', 'callback', 'end_call'] as const;
export type ComponentSharedExit = (typeof COMPONENT_SHARED_EXITS)[number];

export const componentOutcomeSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    terminal: z.boolean().optional(),
    /** The call is over after this ending, on purpose (a transfer). */
    ends: z.boolean().optional(),
    shared: z.enum(COMPONENT_SHARED_EXITS).optional(),
  })
  .strict();
export type ComponentOutcome = z.infer<typeof componentOutcomeSchema>;

export const componentFieldOptionSchema = z
  .object({ value: z.string().min(1), label: z.string().min(1) })
  .strict();
export type ComponentFieldOption = z.infer<typeof componentFieldOptionSchema>;

const fieldConditionSchema = z
  .object({
    path: z.string().min(1),
    equals: z.union([z.string(), z.number(), z.boolean()]),
  })
  .strict();

interface FieldBase {
  path: string;
  label: string;
  help?: string;
  when?: z.infer<typeof fieldConditionSchema>;
  /** Only VagusX ops may change it; a practice never sees it. */
  ops?: boolean;
}

export type ComponentField =
  | (FieldBase & { kind: 'text' | 'textarea' | 'number' | 'duration' | 'boolean' })
  | (FieldBase & {
      kind: 'select' | 'multi';
      options?: ComponentFieldOption[];
      source?: ComponentOptionSource;
    })
  | (FieldBase & {
      kind: 'locale-text';
      storage: LocaleTextStorage;
      /** Placeholders this line may use beyond the call's facts. */
      vars?: string[];
      /** `false` when only `vars` are in scope, none of the call's facts. */
      callFacts?: boolean;
      /** The slot the component reads when the config names none. */
      defaultSlot?: string;
    })
  | (FieldBase & { kind: 'field-list'; item: ComponentField[] })
  | (FieldBase & { kind: 'runtime-key'; multiple?: boolean })
  | (FieldBase & { kind: 'custom'; editor: ComponentCustomEditor });

const fieldBase = {
  path: z.string().min(1),
  label: z.string().min(1),
  help: z.string().min(1).optional(),
  when: fieldConditionSchema.optional(),
  ops: z.boolean().optional(),
};

export const componentFieldSchema: z.ZodType<ComponentField> = z.lazy(() =>
  z.union([
    z
      .object({ ...fieldBase, kind: z.enum(['text', 'textarea', 'number', 'duration', 'boolean']) })
      .strict(),
    z
      .object({
        ...fieldBase,
        kind: z.enum(['select', 'multi']),
        options: z.array(componentFieldOptionSchema).optional(),
        source: z.enum(COMPONENT_OPTION_SOURCES).optional(),
      })
      .strict()
      .refine((f) => Boolean(f.options) !== Boolean(f.source), {
        message: 'a choice field has exactly one of options and source',
      }),
    z
      .object({
        ...fieldBase,
        kind: z.literal('locale-text'),
        storage: z.enum(LOCALE_TEXT_STORAGE),
        vars: z.array(z.string().min(1)).optional(),
        callFacts: z.boolean().optional(),
        defaultSlot: z.string().min(1).optional(),
      })
      .strict(),
    z
      .object({ ...fieldBase, kind: z.literal('field-list'), item: z.array(componentFieldSchema) })
      .strict(),
    z
      .object({ ...fieldBase, kind: z.literal('runtime-key'), multiple: z.boolean().optional() })
      .strict(),
    z
      .object({ ...fieldBase, kind: z.literal('custom'), editor: z.enum(COMPONENT_CUSTOM_EDITORS) })
      .strict(),
  ])
);

export const componentMetaSchema = z
  .object({
    title: z.string().min(1),
    summary: z.string().min(1),
    category: z.enum(COMPONENT_CATEGORIES),
    channels: z.array(z.enum(COMPONENT_CHANNELS)).min(1),
    directions: z.array(z.enum(COMPONENT_CALL_DIRECTIONS)).min(1),
    outcomes: z.array(componentOutcomeSchema).min(1),
    reads: z.array(z.string().min(1)).optional(),
    ui: z.array(componentFieldSchema),
    defaults: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
  })
  .strict();
export type ComponentMeta = z.infer<typeof componentMetaSchema>;

/**
 * What a builder test can ask the tester for (ADR-306). Mirrors agent-core's
 * `TEST_INPUT_KINDS`; the API's vocabulary test holds the two together.
 */
export const COMPONENT_TEST_INPUT_KINDS = [
  'provider',
  'location',
  'appointmentType',
  'existingAppointment',
] as const;
export type ComponentTestInputKind = (typeof COMPONENT_TEST_INPUT_KINDS)[number];

export const componentTestInputSchema = z
  .object({
    kind: z.enum(COMPONENT_TEST_INPUT_KINDS),
    label: z.string().min(1),
    help: z.string().optional(),
    required: z.boolean().optional(),
  })
  .strict();
export type ComponentTestInput = z.infer<typeof componentTestInputSchema>;

export const agentComponentSchema = z
  .object({
    /** The registry key a definition names. Never enumerated client-side. */
    key: z.string().min(1),
    meta: componentMetaSchema,
    /** What a step test of this component asks for before the call. Empty for most. */
    testInputs: z.array(componentTestInputSchema).default([]),
  })
  .strict();
export type AgentComponent = z.infer<typeof agentComponentSchema>;

/**
 * Org-scoped choices for fields whose `source` names them. Only sources some
 * field actually uses are fetched and present.
 */
export const componentOptionListsSchema = z
  .object({
    providers: z.array(componentFieldOptionSchema).optional(),
    locations: z.array(componentFieldOptionSchema).optional(),
    appointmentTypes: z.array(componentFieldOptionSchema).optional(),
  })
  .strict();
export type ComponentOptionLists = z.infer<typeof componentOptionListsSchema>;

/** A fact the call holds before its first step, as `{{path}}` names it. */
export const callFactSchema = z
  .object({
    path: z.string().min(1),
    label: z.string().min(1),
    /** Invented, never a real person. */
    example: z.string(),
    /** The directions on which the call can know it; empty on the others. */
    directions: z.array(z.enum(COMPONENT_CALL_DIRECTIONS)).min(1),
  })
  .strict();
export type CallFact = z.infer<typeof callFactSchema>;

const factList = z.array(z.string().min(1)).min(1).optional();

/** A `Decide` step's settings: its question and the answers it checks, in order. */
export const decideConfigSchema = z
  .object({
    question: z.string().min(1),
    answers: z
      .array(
        z
          .object({
            id: z.string().min(1),
            label: z.string().min(1),
            requires: factList,
            unless: factList,
          })
          .strict()
      )
      .min(1),
    otherwiseLabel: z.string().min(1),
  })
  .strict();
export type DecideConfig = z.infer<typeof decideConfigSchema>;

/** A question the builder offers ready-made, answers written. */
export const decisionPresetSchema = z
  .object({
    key: z.string().min(1),
    directions: z.array(z.enum(COMPONENT_CALL_DIRECTIONS)).min(1),
    hint: z.string().min(1),
    config: decideConfigSchema,
  })
  .strict();
export type DecisionPreset = z.infer<typeof decisionPresetSchema>;

export const agentComponentCatalogSchema = z
  .object({
    /** In palette order. */
    components: z.array(agentComponentSchema),
    /**
     * Outcomes the engine gives ANY node, whatever its component (`gated`: its
     * conditions were not met). Listed once rather than on every component.
     */
    engineOutcomes: z.array(componentOutcomeSchema),
    options: componentOptionListsSchema,
    /** The closed list of facts a line may speak — nothing else is in scope. */
    callFacts: z.array(callFactSchema),
    /** Why the list is what it is, for the picker to show. */
    callFactsNote: z.string().min(1),
    /** Questions a `Decide` step can ask, with their answers written. */
    decisionPresets: z.array(decisionPresetSchema),
  })
  .strict();
export type AgentComponentCatalog = z.infer<typeof agentComponentCatalogSchema>;
